import * as moment from "moment-timezone";
import IOnGetDailyRouteAuditDTO, {
    AuditFlag,
    DailyRouteEventType,
    IAuditEvent,
    IAuditGeoPoint,
    IAuditStop,
} from "../models/DTO/IOnGetDailyRouteAuditDTO";

/** A pin on a static map image. */
export interface IStaticMapMarker {
    point: IAuditGeoPoint;
    /** Google Static Maps named color (blue, green, red, ...) or 0xRRGGBB. */
    color: string;
    /** Single uppercase letter or digit shown on the pin; omitted for small pins. */
    label?: string;
    size?: "tiny" | "mid" | "small";
}

const STATIC_MAP_BASE = "https://maps.googleapis.com/maps/api/staticmap";

/** Google Static Maps rejects request URLs longer than this. */
export const STATIC_MAP_MAX_URL_LENGTH = 16384;

/** Customer delivery location pin. */
export const MARKER_COLOR_CUSTOMER = "blue";
/** Driver GPS fix when marked arrived. */
export const MARKER_COLOR_ARRIVED = "green";
/** Driver GPS fix when marked completed / failed / skipped. */
export const MARKER_COLOR_COMPLETED = "red";
/** Route start / end fixes. */
export const MARKER_COLOR_ROUTE = "gray";

/**
 * Build a Google Static Maps URL for a set of pins. With one pin the map is centered on it
 * at street zoom; with several, Google fits the viewport to all of them. Pins that share a
 * style are sent as one `markers` parameter to keep the URL short; a URL that would still
 * exceed Google's length limit yields null so the caller can render its fallback.
 */
export const buildStaticMapUrl = (markers: IStaticMapMarker[], apiKey: string, size = "640x320", scale = 2): string | null => {
    const usable = markers.filter((m) => m.point && Number.isFinite(m.point.lat) && Number.isFinite(m.point.lng));

    if (usable.length === 0) {
        return null;
    }

    const params: string[] = [`size=${size}`, `scale=${scale}`, "maptype=roadmap"];

    if (usable.length === 1) {
        params.push(`center=${usable[0].point.lat},${usable[0].point.lng}`, "zoom=16");
    }

    // Insertion order is preserved, so the first pin of each style decides where the group lands.
    const groups = new Map<string, string[]>();

    for (const marker of usable) {
        const style: string[] = [`color:${marker.color}`];

        if (marker.size) {
            style.push(`size:${marker.size}`);
        }

        if (marker.label && /^[A-Z0-9]$/.test(marker.label)) {
            style.push(`label:${marker.label}`);
        }

        const key = style.join("|");
        const locations = groups.get(key) ?? [];
        locations.push(`${marker.point.lat},${marker.point.lng}`);
        groups.set(key, locations);
    }

    groups.forEach((locations, key) => {
        params.push(`markers=${encodeURIComponent([key, ...locations].join("|"))}`);
    });

    params.push(`key=${apiKey}`);

    const url = `${STATIC_MAP_BASE}?${params.join("&")}`;

    return url.length <= STATIC_MAP_MAX_URL_LENGTH ? url : null;
};

/** Pins for one stop: the customer location plus the driver's arrival / completion fixes. */
export const buildStopMarkers = (stop: IAuditStop): IStaticMapMarker[] => {
    const markers: IStaticMapMarker[] = [];

    if (stop.ship_to?.location) {
        markers.push({ point: stop.ship_to.location, color: MARKER_COLOR_CUSTOMER, label: "C" });
    }

    if (stop.arrived_location) {
        markers.push({ point: stop.arrived_location, color: MARKER_COLOR_ARRIVED, label: "A" });
    }

    if (stop.completed_location) {
        markers.push({ point: stop.completed_location, color: MARKER_COLOR_COMPLETED, label: "D" });
    }

    return markers;
};

/**
 * Pins for the whole route: every geocoded customer (numbered 1-9, then unlabeled), each
 * driver fix as a small pin, and the route start / end fixes.
 */
export const buildRouteMarkers = (audit: IOnGetDailyRouteAuditDTO): IStaticMapMarker[] => {
    const markers: IStaticMapMarker[] = [];

    if (audit.route.started_location) {
        markers.push({ point: audit.route.started_location, color: MARKER_COLOR_ROUTE, label: "S" });
    }

    for (const stop of audit.stops) {
        if (stop.ship_to?.location) {
            markers.push({
                point: stop.ship_to.location,
                color: MARKER_COLOR_CUSTOMER,
                label: stop.display_seq <= 9 ? String(stop.display_seq) : undefined,
            });
        }

        const fix = stop.arrived_location || stop.completed_location;

        if (fix) {
            markers.push({ point: fix, color: stop.arrived_location ? MARKER_COLOR_ARRIVED : MARKER_COLOR_COMPLETED, size: "small" });
        }
    }

    if (audit.route.completed_location) {
        markers.push({ point: audit.route.completed_location, color: MARKER_COLOR_ROUTE, label: "E" });
    }

    return markers;
};

/** Render an ISO timestamp in the report timezone; empty string when missing. */
export const formatAuditTime = (iso: string | null | undefined, timezone: string, format = "MMM D, YYYY h:mm:ss A z"): string => {
    if (!iso) {
        return "";
    }

    const m = moment.utc(iso);

    return m.isValid() ? m.tz(timezone).format(format) : "";
};

/** Minutes -> "1h 05m" / "12m"; empty for null. */
export const formatMinutes = (minutes: number | null | undefined): string => {
    if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) {
        return "";
    }

    const sign = minutes < 0 ? "-" : "";
    const abs = Math.abs(Math.round(minutes));
    const hours = Math.floor(abs / 60);
    const mins = abs % 60;

    if (hours === 0) {
        return `${sign}${mins}m`;
    }

    return `${sign}${hours}h ${String(mins).padStart(2, "0")}m`;
};

/** Meters -> "85 m" / "1.2 km"; empty for null. */
export const formatDistance = (meters: number | null | undefined): string => {
    if (meters === null || meters === undefined || !Number.isFinite(meters)) {
        return "";
    }

    if (meters < 1000) {
        return `${Math.round(meters)} m`;
    }

    return `${(meters / 1000).toFixed(meters < 10000 ? 1 : 0)} km`;
};

/** "40.712800, -74.006000 (±8 m)" */
export const formatGeoPoint = (point: IAuditGeoPoint | null | undefined): string => {
    if (!point) {
        return "";
    }

    const base = `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`;

    return point.accuracy_m != null ? `${base} (±${Math.round(point.accuracy_m)} m)` : base;
};

/** Translation key for an audit flag. */
export const flagLabelKey = (flag: AuditFlag): string => `audit_flag_${flag}`;

/** Translation key for an event type. */
export const eventLabelKey = (eventType: DailyRouteEventType | string): string => `audit_event_${eventType}`;

/** Flags that an auditor should treat as exceptions (red) rather than informational (amber). */
export const SEVERE_FLAGS: AuditFlag[] = [
    "far_from_customer",
    "no_signature",
    "missing_status_reason",
    "completed_without_arrival",
    "not_completed",
    "photos_removed",
];

export const isSevereFlag = (flag: AuditFlag): boolean => SEVERE_FLAGS.includes(flag);

/** Every event on the route in chronological order (device time, then server time). */
export const sortedRouteTimeline = (audit: IOnGetDailyRouteAuditDTO): IAuditEvent[] => {
    const all = [...audit.route_events, ...audit.stops.flatMap((s) => s.events)];

    return all.sort((a, b) => {
        const ta = a.event_at ? Date.parse(a.event_at) : 0;
        const tb = b.event_at ? Date.parse(b.event_at) : 0;

        if (ta !== tb) return ta - tb;

        const ra = a.recorded_at ? Date.parse(a.recorded_at) : 0;
        const rb = b.recorded_at ? Date.parse(b.recorded_at) : 0;

        return ra - rb;
    });
};

/**
 * How event metadata is rendered: `t` resolves a translation key (with ICU values) and
 * `formatAmount` renders a money amount in the report's currency.
 */
export interface IEventMetadataFormatter {
    t: (key: string, values?: Record<string, string | number>) => string;
    formatAmount: (value: number) => string;
}

/** Short, human-readable rendering of an event's metadata for the timeline table. */
export const describeEventMetadata = (event: IAuditEvent, fmt: IEventMetadataFormatter): string => {
    const meta = event.metadata;

    if (!meta) {
        return "";
    }

    const parts: string[] = [];

    if (typeof meta.signed_by_name === "string" && meta.signed_by_name)
        parts.push(fmt.t("audit_meta_signed_by", { name: meta.signed_by_name }));
    if (typeof meta.name === "string" && meta.name) parts.push(meta.name);
    if (typeof meta.driver_notes === "string") parts.push(meta.driver_notes ? `"${meta.driver_notes}"` : fmt.t("audit_meta_note_cleared"));
    if (typeof meta.amount === "number") parts.push(fmt.formatAmount(meta.amount));
    if (typeof meta.reference === "string" && meta.reference) parts.push(fmt.t("audit_meta_reference", { reference: meta.reference }));
    if (typeof meta.vendor_name === "string" && meta.vendor_name) parts.push(meta.vendor_name);
    if (typeof meta.admin_name === "string" && meta.admin_name) parts.push(meta.admin_name);
    if (typeof meta.skipped_stop_count === "number") parts.push(fmt.t("audit_meta_stops_auto_skipped", { count: meta.skipped_stop_count }));
    if (typeof meta.line_count === "number") parts.push(fmt.t("audit_meta_line_count", { count: meta.line_count }));
    if (meta.is_cold_call === true) parts.push(fmt.t("cold_call"));

    return parts.join(" · ");
};

/** Pick the one stop addressed by ?stop=<id>, or null when not found. */
export const findStop = (audit: IOnGetDailyRouteAuditDTO, stopId: number | null): IAuditStop | null => {
    if (stopId === null || !Number.isFinite(stopId)) {
        return null;
    }

    return audit.stops.find((s) => s.stop_id === stopId) || null;
};
